"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PresetsService } from "@/generated";
import { useModels } from "@/hooks/useModels";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { icons, SquareLibrary } from "lucide-react";
import ImageWithFallback from "@/components/ImageWithFallback";
import { resolvePresetIcon } from "@/utils/presetIcons";
import { RetirementNotice } from "@/components/Views/Models/RetirementNotice";

type ModelSelectCompactProps = {
  value: unknown;
  display: Record<string, unknown> | undefined;
};

type ModelDisplay = {
  title: string;
  image?: string | null;
  retirementDate?: string | null;
  replacedBy?: string | null;
};

type PresetDisplay = {
  title: string;
  icon?: string;
};

const PRESET_PREFIX = "preset:";

function normalizeModelValue(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string") return [value];
  return [];
}

function isPresetRef(values: string[]): boolean {
  return values.length === 1 && values[0].startsWith(PRESET_PREFIX);
}

function humanizeHandle(handle: string): string {
  return handle
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const Empty = () => <span className="italic text-neutral-400">-</span>;

function ModelLogo({ image, title }: { image?: string | null; title: string }) {
  return (
    <div className="relative h-3.5 w-3.5 shrink-0">
      <ImageWithFallback
        fill
        src={image || "/images/placeholder.svg"}
        alt={title}
        className="dark:invert dark:brightness-0"
      />
    </div>
  );
}

function ModelLabel({ model }: { model: ModelDisplay }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <ModelLogo image={model.image} title={model.title} />
      <span className="truncate" title={model.title}>
        {model.title}
      </span>
      <RetirementNotice
        retirementDate={model.retirementDate}
        replacedBy={model.replacedBy}
      />
    </span>
  );
}

function PresetLabel({ preset }: { preset: PresetDisplay }) {
  const Icon = resolvePresetIcon(preset.icon) ?? SquareLibrary;

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate" title={preset.title}>
        {preset.title}
      </span>
    </span>
  );
}

export function ModelSelectCompact({
  value,
  display,
}: ModelSelectCompactProps) {
  const { spotGroup } = useSpotUser();
  const models = normalizeModelValue(value);
  const endpoint = (display?.endpoint as string | undefined) ?? "/models/llms";
  const presetMode = isPresetRef(models);

  const { data: modelsData } = useModels(
    endpoint,
    !presetMode && models.length > 0
  );

  const { data: presetsResponse } = useQuery({
    queryKey: [DATA_KEYS.MODEL_PRESETS, spotGroup.id],
    queryFn: () =>
      PresetsService.listPresets({
        path: { group_id: spotGroup.id },
      }),
    staleTime: 5 * 60 * 1000,
    enabled: presetMode,
  });

  const modelByName = useMemo(() => {
    const map = new Map<string, ModelDisplay>();
    for (const model of modelsData ?? []) {
      map.set(model.name, {
        title: model.title || model.name,
        image: model.image,
        retirementDate: model.retirement_date,
        replacedBy: model.replaced_by
          ? (modelsData?.find((m) => m.name === model.replaced_by)?.title ??
            model.replaced_by)
          : null,
      });
    }
    return map;
  }, [modelsData]);

  const presetByHandle = useMemo(() => {
    const map = new Map<string, PresetDisplay>();
    for (const preset of presetsResponse?.data ?? []) {
      map.set(preset.handle, {
        title: preset.title || preset.handle,
        icon: preset.icon,
      });
    }
    return map;
  }, [presetsResponse]);

  if (models.length === 0) return <Empty />;

  if (presetMode) {
    const handle = models[0].slice(PRESET_PREFIX.length);
    const preset = presetByHandle.get(handle) ?? {
      title: humanizeHandle(handle),
    };
    return <PresetLabel preset={preset} />;
  }

  const resolvedModels = models.map(
    (name) =>
      modelByName.get(name) ?? {
        title: humanizeHandle(name),
        image: null,
      }
  );
  const [first, ...rest] = resolvedModels;

  if (rest.length === 0) {
    return <ModelLabel model={first} />;
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <ModelLabel model={first} />
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="shrink-0 cursor-default rounded-sm border border-neutral-200 bg-neutral-50 px-1.5 py-px text-[10px] text-neutral-600">
            +{rest.length}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <ul className="flex flex-col gap-1">
            {rest.map((model, index) => (
              <li key={models[index + 1]} className="flex items-center gap-1.5">
                <ModelLogo image={model.image} title={model.title} />
                <span>{model.title}</span>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
